/**
 * QuantX Analytics API Service
 *
 * Provides frontend access to QuantX Analytics endpoints on DataFeed Server.
 * All endpoints require JWT Bearer token authentication.
 *
 * @see docs/HANDOFF_QuantX_Integration.md for full API documentation
 */

import axios from 'axios';
import { dataFeedURL } from '../config';

// Base URL for QuantX endpoints
const QUANTX_BASE_URL = `${dataFeedURL}/api/quantx`;

// Request timeout (5 seconds)
const REQUEST_TIMEOUT = 5000;

// Performance warning threshold (ms)
const SLOW_REQUEST_THRESHOLD = 3000;

/**
 * Create axios instance with default config
 */
const createAxiosInstance = (token) => {
    return axios.create({
        baseURL: QUANTX_BASE_URL,
        timeout: REQUEST_TIMEOUT,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    });
};

/**
 * Log slow requests for performance monitoring
 */
const logPerformance = (endpoint, startTime) => {
    const duration = Date.now() - startTime;
    if (duration > SLOW_REQUEST_THRESHOLD) {
        console.warn(`[QuantX API] Slow request: ${endpoint} took ${duration}ms`);
    }
    return duration;
};

/**
 * Handle API errors consistently
 */
const handleError = (error, endpoint) => {
    if (error.response) {
        // Server responded with error status
        console.error(`[QuantX API] ${endpoint} failed:`, error.response.status, error.response.data);
        throw {
            status: error.response.status,
            message: error.response.data?.message || error.response.data?.error || 'Request failed',
            endpoint
        };
    } else if (error.request) {
        // No response received
        console.error(`[QuantX API] ${endpoint} - No response:`, error.message);
        throw {
            status: 0,
            message: 'No response from server. Check if DataFeed Server is running.',
            endpoint
        };
    } else {
        // Request setup error
        console.error(`[QuantX API] ${endpoint} - Request error:`, error.message);
        throw {
            status: -1,
            message: error.message,
            endpoint
        };
    }
};

/**
 * GET /api/quantx/current-state/{symbol}
 *
 * Returns real-time trend snapshot including EMA positions, ADX, and session info.
 *
 * @param {string} token - JWT authentication token
 * @param {string} symbol - Trading symbol (e.g., 'XAUUSD_ct')
 * @returns {Promise<Object>} Current state data
 */
export const getCurrentState = async (token, symbol) => {
    const startTime = Date.now();
    const endpoint = `/current-state/${symbol}`;

    try {
        const api = createAxiosInstance(token);
        const response = await api.get(endpoint);
        logPerformance(endpoint, startTime);
        return response.data;
    } catch (error) {
        handleError(error, endpoint);
    }
};

/**
 * GET /api/quantx/strategy-signal/{symbol}
 *
 * Returns trading signal with confidence score and reasons.
 *
 * @param {string} token - JWT authentication token
 * @param {string} symbol - Trading symbol
 * @returns {Promise<Object>} Strategy signal data
 */
export const getStrategySignal = async (token, symbol) => {
    const startTime = Date.now();
    const endpoint = `/strategy-signal/${symbol}`;

    try {
        const api = createAxiosInstance(token);
        const response = await api.get(endpoint);
        logPerformance(endpoint, startTime);
        return response.data;
    } catch (error) {
        handleError(error, endpoint);
    }
};

/**
 * GET /api/quantx/strategy-dashboard/{symbol}
 *
 * Returns complete dashboard data for pivot table display.
 * Includes trend analysis, Fibonacci levels, ADX analysis, and session info.
 *
 * @param {string} token - JWT authentication token
 * @param {string} symbol - Trading symbol
 * @returns {Promise<Object>} Full dashboard data
 */
export const getStrategyDashboard = async (token, symbol) => {
    const startTime = Date.now();
    const endpoint = `/strategy-dashboard/${symbol}`;

    try {
        const api = createAxiosInstance(token);
        const response = await api.get(endpoint);
        logPerformance(endpoint, startTime);
        return response.data;
    } catch (error) {
        handleError(error, endpoint);
    }
};

/**
 * GET /api/quantx/trend-filter-stats/{symbol}
 *
 * Returns EMA bars-above analysis for trend filtering.
 *
 * @param {string} token - JWT authentication token
 * @param {string} symbol - Trading symbol
 * @returns {Promise<Object>} Trend filter statistics
 */
export const getTrendFilterStats = async (token, symbol) => {
    const startTime = Date.now();
    const endpoint = `/trend-filter-stats/${symbol}`;

    try {
        const api = createAxiosInstance(token);
        const response = await api.get(endpoint);
        logPerformance(endpoint, startTime);
        return response.data;
    } catch (error) {
        handleError(error, endpoint);
    }
};

/**
 * GET /api/quantx/fib-performance/{symbol}
 *
 * Returns Fibonacci level statistics and performance data.
 *
 * @param {string} token - JWT authentication token
 * @param {string} symbol - Trading symbol
 * @returns {Promise<Object>} Fibonacci performance data
 */
export const getFibPerformance = async (token, symbol) => {
    const startTime = Date.now();
    const endpoint = `/fib-performance/${symbol}`;

    try {
        const api = createAxiosInstance(token);
        const response = await api.get(endpoint);
        logPerformance(endpoint, startTime);
        return response.data;
    } catch (error) {
        handleError(error, endpoint);
    }
};

/**
 * GET /api/quantx/adx-analysis/{symbol}
 *
 * Returns ADX/ATR volatility analysis data.
 * DEP-2 UPDATE: Now includes D1 timeframe data alongside H4.
 *
 * Response format:
 * {
 *   h4: { adx: 28.5, atr: 12.3, range: "GOOD_TREND" },
 *   d1: { adx: 32.1, atr: 45.8, range: "STRONG_TREND" },
 *   regime: "TRENDING_UP"
 * }
 *
 * @param {string} token - JWT authentication token
 * @param {string} symbol - Trading symbol
 * @returns {Promise<Object>} ADX analysis data with H4 and D1 timeframes
 */
export const getAdxAnalysis = async (token, symbol) => {
    const startTime = Date.now();
    const endpoint = `/adx-analysis/${symbol}`;

    try {
        const api = createAxiosInstance(token);
        const response = await api.get(endpoint);
        logPerformance(endpoint, startTime);
        return response.data;
    } catch (error) {
        handleError(error, endpoint);
    }
};

/**
 * GET /api/quantx/fib-levels/{symbol}
 *
 * DEP-1 NEW: Returns current Fibonacci swing levels and price positions.
 * This endpoint provides real-time Fibonacci data for the FibCompactTable.
 *
 * Query Parameters:
 * - timeframe: H4 | D1 (default: H4)
 *
 * Response format:
 * {
 *   swingHigh: 2650.50,
 *   swingLow: 2580.25,
 *   timeframe: "H4",
 *   nearestLevel: "38.2%",
 *   distanceToNearest: 12.5,
 *   levels: [
 *     { level: "0.0%", price: 2650.50, status: "ABOVE" },
 *     { level: "23.6%", price: 2633.93, status: "ABOVE" },
 *     { level: "38.2%", price: 2623.47, status: "AT" },
 *     { level: "50.0%", price: 2615.38, status: "BELOW" },
 *     { level: "61.8%", price: 2607.28, status: "BELOW" },
 *     { level: "78.6%", price: 2595.41, status: "BELOW" },
 *     { level: "100.0%", price: 2580.25, status: "BELOW" }
 *   ]
 * }
 *
 * @param {string} token - JWT authentication token
 * @param {string} symbol - Trading symbol
 * @param {string} timeframe - Timeframe (H4 or D1, default: H4)
 * @returns {Promise<Object>} Fibonacci levels with swing data
 */
export const getFibLevels = async (token, symbol, timeframe = 'H4') => {
    const startTime = Date.now();
    const endpoint = `/fib-levels/${symbol}?timeframe=${timeframe}`;

    try {
        const api = createAxiosInstance(token);
        const response = await api.get(endpoint);
        logPerformance(endpoint, startTime);
        return response.data;
    } catch (error) {
        handleError(error, endpoint);
    }
};

/**
 * GET /api/quantx/ai-context/{symbol}
 *
 * Returns AI context data for Q.AI integration.
 * Contains currentState, historicalContext (future), and contextText.
 *
 * @param {string} token - JWT authentication token
 * @param {string} symbol - Trading symbol
 * @returns {Promise<Object>} AI context data
 */
export const getAIContext = async (token, symbol) => {
    const startTime = Date.now();
    const endpoint = `/ai-context/${symbol}`;

    try {
        const api = createAxiosInstance(token);
        const response = await api.get(endpoint);
        logPerformance(endpoint, startTime);
        return response.data;
    } catch (error) {
        handleError(error, endpoint);
    }
};

/**
 * POST /api/quantx/backfill/{symbol}
 *
 * Admin endpoint to generate test data for a symbol.
 *
 * @param {string} token - JWT authentication token
 * @param {string} symbol - Trading symbol
 * @returns {Promise<Object>} Backfill result
 */
export const triggerBackfill = async (token, symbol) => {
    const startTime = Date.now();
    const endpoint = `/backfill/${symbol}`;

    try {
        const api = createAxiosInstance(token);
        const response = await api.post(endpoint);
        logPerformance(endpoint, startTime);
        return response.data;
    } catch (error) {
        handleError(error, endpoint);
    }
};

/**
 * GET /api/quantx/debug/ohlc-status/{symbol}
 *
 * Admin endpoint to check OHLC data status for debugging.
 *
 * @param {string} token - JWT authentication token
 * @param {string} symbol - Trading symbol
 * @returns {Promise<Object>} OHLC status data
 */
export const getOhlcStatus = async (token, symbol) => {
    const startTime = Date.now();
    const endpoint = `/debug/ohlc-status/${symbol}`;

    try {
        const api = createAxiosInstance(token);
        const response = await api.get(endpoint);
        logPerformance(endpoint, startTime);
        return response.data;
    } catch (error) {
        handleError(error, endpoint);
    }
};

/**
 * Fetch all QuantX data for a symbol in parallel
 *
 * Fetches current-state, strategy-signal, and strategy-dashboard simultaneously
 * for optimal performance.
 *
 * @param {string} token - JWT authentication token
 * @param {string} symbol - Trading symbol
 * @returns {Promise<Object>} Combined data from all endpoints
 */
export const fetchAllQuantXData = async (token, symbol) => {
    const startTime = Date.now();

    try {
        const [currentState, strategySignal, strategyDashboard] = await Promise.all([
            getCurrentState(token, symbol),
            getStrategySignal(token, symbol),
            getStrategyDashboard(token, symbol)
        ]);

        const duration = Date.now() - startTime;
        console.log(`[QuantX API] fetchAllQuantXData completed in ${duration}ms`);

        return {
            currentState,
            strategySignal,
            strategyDashboard,
            fetchedAt: new Date().toISOString()
        };
    } catch (error) {
        console.error('[QuantX API] fetchAllQuantXData failed:', error);
        throw error;
    }
};

// Default export with all functions
export default {
    getCurrentState,
    getStrategySignal,
    getStrategyDashboard,
    getTrendFilterStats,
    getFibPerformance,
    getFibLevels,      // DEP-1: New endpoint for current Fibonacci levels
    getAdxAnalysis,
    getAIContext,
    triggerBackfill,
    getOhlcStatus,
    fetchAllQuantXData
};
